const triggerSelector = '[data-everide-service-drawer-tab]';
const tabSelector = '[data-everide-service-tab]';
const panelSelector = '[data-everide-service-panel]';

class EverideServiceDrawer extends HTMLElement {
  #drawer;
  #tabs = [];
  #panels = [];

  connectedCallback() {
    this.#drawer = this.querySelector('theme-drawer');
    this.#tabs = [...this.querySelectorAll(tabSelector)];
    this.#panels = [...this.querySelectorAll(panelSelector)];
    if (!this.#drawer || this.#tabs.length === 0) return;

    this.addEventListener('click', this.#handleInternalClick);
    this.addEventListener('keydown', this.#handleTabKeydown);
    document.addEventListener('click', this.#handleExternalTrigger, true);

    const selectedTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.#tabs[0];
    this.#setTab(selectedTab.dataset.everideServiceTab);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleInternalClick);
    this.removeEventListener('keydown', this.#handleTabKeydown);
    document.removeEventListener('click', this.#handleExternalTrigger, true);
    this.#drawer = undefined;
    this.#tabs = [];
    this.#panels = [];
  }

  #handleExternalTrigger = (event) => {
    if (!(event.target instanceof Element) || !this.#drawer) return;

    const trigger = event.target.closest(triggerSelector);
    if (!trigger || trigger.getAttribute('aria-controls') !== this.#drawer.id) return;

    this.#setTab(trigger.dataset.everideServiceDrawerTab);
  };

  #handleInternalClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest(tabSelector);
    if (!tab || !this.contains(tab)) return;

    this.#setTab(tab.dataset.everideServiceTab);
  };

  #handleTabKeydown = (event) => {
    if (!(event.target instanceof Element)) return;

    const currentTab = event.target.closest(tabSelector);
    if (!currentTab || !this.contains(currentTab)) return;

    const currentIndex = this.#tabs.indexOf(currentTab);
    if (currentIndex === -1) return;

    let nextIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.#tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.#tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    const nextTab = this.#tabs[nextIndex];
    this.#setTab(nextTab.dataset.everideServiceTab);
    nextTab.focus();
  };

  #setTab(tabName) {
    if (!tabName || !this.#tabs.some((tab) => tab.dataset.everideServiceTab === tabName)) return;

    for (const tab of this.#tabs) {
      const isActive = tab.dataset.everideServiceTab === tabName;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of this.#panels) {
      panel.hidden = panel.dataset.everideServicePanel !== tabName;
    }
  }
}

if (!customElements.get('everide-service-drawer')) {
  customElements.define('everide-service-drawer', EverideServiceDrawer);
}
